import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subscription, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import { UserService } from 'src/app/services/user.service';

type AccountDeletionStep = 'intro' | 'mobile' | 'delete' | 'success' | 'unavailable';

interface DeleteAccountOption {
  userId: string;
  accountType: string;
  roleName: string;
  label: string;
  canDelete: boolean;
  message?: string;
  fullName?: string;
  mobileNumber?: string | number;
}

@Component({
  selector: 'app-account-deletion',
  templateUrl: './account-deletion.component.html',
  styleUrls: ['./account-deletion.component.scss'],
  providers: [MessageService]
})
export class AccountDeletionComponent implements OnInit, OnDestroy {
  currentStep: AccountDeletionStep = 'intro';
  mobileForm: FormGroup;
  deleteAccountForm: FormGroup;
  submittedMobile = false;
  submittedDeleteAccount = false;
  sendingOtp = false;
  verifyingOtp = false;
  submittingDeleteRequest = false;
  checkingDeleteRequestStatus = false;
  showOtpmodal = false;
  showAccountSelectionModal = false;
  showDeleteRequestStatusModal = false;
  showResendButton = false;
  showOtpErrors = false;
  showOtpErrorsMsg = false;
  showMessageInput = false;
  mobileNumber: string;
  verifiedMobileNumber: string;
  verifiedUserId: string;
  deleteOtpToken: string;
  deleteSessionToken: string;
  accountOptions: DeleteAccountOption[] = [];
  selectedAccount: DeleteAccountOption;
  existingDeleteRequest: any;
  countryCodeList: any[] = [];
  selectedCountryCode: any;
  statusModalTitle = 'Request already submitted';
  statusModalDescription = 'Hi — thanks for letting us know. We found an existing delete-account request for this user, so we didn’t create a duplicate. The request is currently under review, and the account will be deleted within 48 hours. If you need to cancel or change the request, please contact support at support@tplsport.com and we’ll take care of it.';
  otp: string;
  otpErrorMessage: string;
  displayTime = '00:30';
  counter = 30;
  private countDown: Subscription;

  reasonList = [
    { id: 1, label: 'Not happy with us.' },
    { id: 2, label: 'Get another application.' },
    { id: 3, label: 'Not easy to use.' },
    { id: 4, label: 'Other' },
  ];

  selectedReason: string;

  config = {
    allowNumbersOnly: true,
    length: 4,
    isPasswordInput: false,
    disableAutoFocus: false,
    required: true,
    placeholder: '',
    inputStyles: {
      width: '50px',
      height: '50px'
    }
  };

  constructor(
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private userService: UserService,
    private messageService: MessageService
  ) { }

  ngOnInit(): void {
    this.mobileForm = this.formBuilder.group({
      phoneCode: [null, [Validators.required]],
      mobileNumber: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]]
    });

    this.deleteAccountForm = this.formBuilder.group({
      reason: ['', [Validators.required]],
      message: [''],
    });

    this.getCountryCodes();
  }

  ngOnDestroy(): void {
    this.stopOtpTimer();
  }

  get mobileFormControls() {
    return this.mobileForm.controls;
  }

  get deleteReqForm() {
    return this.deleteAccountForm.controls;
  }

  startDeleteRequest(): void {
    this.currentStep = 'mobile';
  }

  sendOtp(mode: 'send' | 'resend' = 'send'): void {
    if (mode === 'send') {
      this.submittedMobile = true;
      if (this.mobileForm.invalid) {
        return;
      }
      this.mobileNumber = this.mobileForm.value.mobileNumber;
      this.verifiedMobileNumber = null;
      this.verifiedUserId = null;
      this.deleteOtpToken = null;
      this.deleteSessionToken = null;
      this.accountOptions = [];
      this.selectedAccount = null;
    }

    if (!this.mobileNumber) {
      return;
    }

    const mobileData = {
      mobileNumber: Number(this.mobileNumber),
      status: true,
      userType: 'guest',
      phoneCode: this.selectedCountryCode?.phone_code || '91',
      countryname: this.selectedCountryCode?.name || 'India',
      countrycode: this.selectedCountryCode?.code || 'IN'
    };

    this.sendingOtp = true;
    this.showOtpErrors = false;
    this.showOtpErrorsMsg = false;
    this.otp = '';

    this.userService.sendDeleteAccountOtp(mobileData).subscribe(
      (data) => {
        this.sendingOtp = false;
        this.deleteOtpToken = data?.data?.deleteOtpToken;
        this.showOtpmodal = true;
        this.showResendButton = false;
        this.startOtpTimer();
        this.messageService.add({
          key: 'toastmsg',
          severity: 'success',
          summary: 'OTP Sent',
          detail: 'OTP has been sent to your registered mobile number.',
          life: 5000
        });
      },
      (err) => {
        this.sendingOtp = false;
        this.messageService.add({
          key: 'toastmsg',
          severity: 'error',
          summary: 'Unable to send OTP',
          detail: err?.error?.error || err?.error?.message || 'Please check the mobile number and try again.',
          life: 6000
        });
      }
    );
  }

  resendOtp(): void {
    this.sendOtp('resend');
  }

  onOtpChange(otp: string): void {
    this.otp = otp;
    this.showOtpErrors = false;
    this.showOtpErrorsMsg = false;
  }

  verifyAndProceed(): void {
    if (!this.otp || this.otp.length < 4) {
      this.showOtpErrors = true;
      this.showOtpErrorsMsg = false;
      return;
    }

    const otpData = {
      otp: this.otp,
      mobileNumber: Number(this.mobileNumber),
      phoneCode: this.selectedCountryCode?.phone_code || '91',
      deleteOtpToken: this.deleteOtpToken
    };

    this.verifyingOtp = true;
    this.userService.verifyDeleteAccountOtp(otpData).subscribe(
      (data) => {
        this.verifyingOtp = false;
        this.verifiedMobileNumber = this.mobileNumber;
        this.deleteSessionToken = data?.data?.deleteSessionToken;
        this.accountOptions = data?.data?.accounts || [];
        this.showOtpmodal = false;
        this.stopOtpTimer();
        this.handleVerifiedAccounts();
      },
      (err) => {
        this.verifyingOtp = false;
        this.showOtpErrorsMsg = true;
        this.otpErrorMessage = err?.error?.error || err?.error?.message || 'Invalid OTP. Please try again.';
      }
    );
  }

  handleVerifiedAccounts(): void {
    if (!this.deleteSessionToken || this.accountOptions.length === 0) {
      this.messageService.add({
        key: 'toastmsg',
        severity: 'error',
        summary: 'Account Not Found',
        detail: 'No account was found for this mobile number.',
        life: 6000
      });
      return;
    }

    if (this.accountOptions.length > 1) {
      this.showAccountSelectionModal = true;
      return;
    }

    this.selectAccountForDeletion(this.accountOptions[0]);
  }

  selectAccountForDeletion(account: DeleteAccountOption): void {
    this.selectedAccount = account;
    this.verifiedUserId = account?.userId;
    this.showAccountSelectionModal = false;

    if (!account?.canDelete) {
      this.showUnavailableAccount(account);
      return;
    }

    this.checkExistingDeleteRequest(() => {
      this.currentStep = 'delete';
      this.messageService.add({
        key: 'toastmsg',
        severity: 'success',
        summary: 'Mobile Verified',
        detail: 'You can now submit your account deletion request.',
        life: 5000
      });
    });
  }

  showUnavailableAccount(account?: DeleteAccountOption): void {
    const errorMessage = account?.message || 'This account cannot request account deletion from this page.';
    this.existingDeleteRequest = {
      mobileNumber: this.verifiedMobileNumber,
      status: 'Not eligible'
    };
    this.statusModalTitle = 'Account not eligible for deletion';
    this.statusModalDescription = errorMessage;
    this.currentStep = 'unavailable';
    this.showDeleteRequestStatusModal = true;
  }

  onReasonSelect(event): void {
    if (event.value.id === 4) {
      this.showMessageInput = true;
    } else {
      this.showMessageInput = false;
      this.deleteAccountForm.get('message').setValue('');
    }
    this.selectedReason = event.value.label;
  }

  submitDeleteAccount(): void {
    this.submittedDeleteAccount = true;
    if (this.deleteAccountForm.invalid || !this.verifiedMobileNumber || !this.selectedAccount) {
      return;
    }

    if (!this.selectedAccount.canDelete) {
      this.showUnavailableAccount(this.selectedAccount);
      return;
    }

    this.checkExistingDeleteRequest(() => this.createDeleteAccountRequest());
  }

  private createDeleteAccountRequest(): void {
    const deleteAccountReqData: any = this.deleteAccountForm.value;
    deleteAccountReqData.reason = this.selectedReason;
    deleteAccountReqData.mobileNumber = Number(this.verifiedMobileNumber);
    deleteAccountReqData.phoneCode = this.selectedCountryCode?.phone_code || '91';
    deleteAccountReqData.countryname = this.selectedCountryCode?.name || 'India';
    deleteAccountReqData.countrycode = this.selectedCountryCode?.code || 'IN';
    deleteAccountReqData.deleteSessionToken = this.deleteSessionToken;
    deleteAccountReqData.selectedUserId = this.selectedAccount.userId;
    deleteAccountReqData.selectedAccountType = this.selectedAccount.accountType;
    deleteAccountReqData.userId = this.selectedAccount.userId;

    this.submittingDeleteRequest = true;
    this.userService.submitSelectedDeleteAccountRequest(deleteAccountReqData).subscribe(
      (data) => {
        this.submittingDeleteRequest = false;
        this.existingDeleteRequest = this.getDeleteRequestFromResponse(data) || {
          mobileNumber: this.verifiedMobileNumber,
          reason: this.selectedReason,
          message: this.deleteAccountForm.value.message,
          status: 'Submitted'
        };
        this.statusModalTitle = 'Request submitted';
        this.statusModalDescription = 'Your delete account request has been submitted successfully.';
        this.currentStep = 'success';
        this.showDeleteRequestStatusModal = true;
        this.messageService.add({
          key: 'toastmsg',
          severity: 'success',
          summary: 'Request Submitted',
          detail: 'Delete Account Request sent successfully. Account will be deleted within 48 hrs.',
          life: 6000
        });
      },
      (err) => {
        this.submittingDeleteRequest = false;
        const errorMessage = this.getApiErrorMessage(err, 'Unable to submit delete account request.');

        if (err?.status === 403) {
          this.existingDeleteRequest = {
            mobileNumber: this.verifiedMobileNumber,
            status: 'Not eligible'
          };
          this.statusModalTitle = 'Account not eligible for deletion';
          this.statusModalDescription = errorMessage;
          this.currentStep = 'unavailable';
          this.showDeleteRequestStatusModal = true;
        }

        this.messageService.add({
          key: 'toastmsg',
          severity: 'error',
          summary: 'Request Failed',
          detail: errorMessage,
          life: 6000
        });
      }
    );
  }

  private checkExistingDeleteRequest(onNoExistingRequest: () => void): void {
    if (!this.verifiedUserId) {
      onNoExistingRequest();
      return;
    }

    this.checkingDeleteRequestStatus = true;
    this.userService.getUserDeleteRequestPublic(this.verifiedUserId).subscribe(
      (data) => {
        this.checkingDeleteRequestStatus = false;
        const deleteRequest = this.getDeleteRequestFromResponse(data);

        if (deleteRequest) {
          this.existingDeleteRequest = deleteRequest;
          this.statusModalTitle = 'Request already submitted';
          this.statusModalDescription = 'Hi — thanks for letting us know. We found an existing delete-account request for this user, so we didn’t create a duplicate. The request is currently under review, and the account will be deleted within 48 hours. If you need to cancel or change the request, please contact support at support@tplsport.com and we’ll take care of it.';
          this.currentStep = 'success';
          this.showDeleteRequestStatusModal = true;
          return;
        }

        onNoExistingRequest();
      },
      () => {
        this.checkingDeleteRequestStatus = false;
        onNoExistingRequest();
      }
    );
  }

  getCountryCodes(): void {
    this.commonService.getCountryList().subscribe((data: any) => {
      this.countryCodeList = data?.data?.items || [];
      this.setInitialCountryCode();
    });
  }

  setInitialCountryCode(): void {
    this.selectedCountryCode = this.countryCodeList.find(ele => ele.name === 'India') || this.countryCodeList[0];
    this.mobileForm.get('phoneCode').setValue(this.selectedCountryCode);
  }

  setCountryCode(event): void {
    this.selectedCountryCode = event.value;
  }

  private getDeleteRequestFromResponse(response): any {
    if (!response) {
      return null;
    }

    const data = response?.data || response;
    const dataKeys = Object.keys(data);

    if (data?.success === false || (dataKeys.length === 1 && data?.message)) {
      return null;
    }

    if (data?.message && data?.id && !data?.reason && !data?.mobileNumber && !data?.created_at && !data?.createdAt) {
      return null;
    }

    if (Array.isArray(data)) {
      return data.length > 0 ? data[0] : null;
    }

    if (Array.isArray(data?.items)) {
      return data.items.length > 0 ? data.items[0] : null;
    }

    if (Array.isArray(data?.data?.items)) {
      return data.data.items.length > 0 ? data.data.items[0] : null;
    }

    if (data?.item) {
      return data.item;
    }

    if (data?.deleteAccountRequest) {
      return data.deleteAccountRequest;
    }

    return dataKeys.length > 0 ? data : null;
  }

  private getApiErrorMessage(error: any, fallback: string): string {
    return error?.error?.error || error?.error?.message || fallback;
  }

  private startOtpTimer(): void {
    this.stopOtpTimer();
    this.counter = 30;
    this.displayTime = this.transformTimer(this.counter);
    this.countDown = timer(1000, 1000).pipe(take(this.counter)).subscribe(() => {
      this.counter--;
      this.displayTime = this.transformTimer(this.counter);
      if (this.counter === 0) {
        this.showResendButton = true;
        this.stopOtpTimer();
      }
    });
  }

  private stopOtpTimer(): void {
    if (this.countDown) {
      this.countDown.unsubscribe();
      this.countDown = null;
    }
  }

  private transformTimer(value: number): string {
    const minutes = Math.floor(value / 60);
    const seconds = Math.floor(value - minutes * 60);
    return ('00' + minutes).slice(-2) + ':' + ('00' + seconds).slice(-2);
  }
}
